// Passwordless sign-in. Ask for an email; following its link lands here with a
// token, or typing its code here does the same. The code exists because an app
// installed to an iOS home screen keeps its own cookies: the link would sign in
// Safari and leave the app signed out.

import { type FormEvent, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ApiError, api } from "../api/client";
import { releaseCacheUnlessOwnedBy } from "../storage/me";
import { APP_NAME } from "../ui/brand";
import { Logo } from "../ui/Icon";

type Phase =
  | { kind: "form" }
  | { kind: "sending" }
  | { kind: "sent"; email: string }
  | { kind: "checking"; email: string }
  | { kind: "redeeming" }
  | { kind: "error"; message: string };

export function SignIn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>(token ? { kind: "redeeming" } : { kind: "form" });

  /** The same ending for the link and the code. */
  const finish = async (signedInAs: string) => {
    await releaseCacheUnlessOwnedBy(signedInAs);
    // The gate reads this to survive an unreachable server; a fresh sign-in
    // must not leave a stale identity behind it.
    try {
      localStorage.removeItem("metis.me");
    } catch {
      // nothing to clear
    }
    navigate("/", { replace: true });
  };

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    api
      .post<{ email: string }>("/api/auth/callback", { token })
      .then((body) => {
        if (!cancelled) void finish(body.email);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const message =
          e instanceof ApiError ? e.message : "Could not reach the server. Try the link again.";
        setPhase({ kind: "error", message });
      });
    return () => {
      cancelled = true;
    };
    // `finish` only navigates; the token is what this effect is about.
  }, [token]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const address = email.trim();
    if (!address) return;
    setPhase({ kind: "sending" });
    try {
      await api.post("/api/auth/magic-link", { email: address });
      setCode("");
      setCodeError(null);
      setPhase({ kind: "sent", email: address });
    } catch (err) {
      setPhase({
        kind: "error",
        message: err instanceof ApiError ? err.message : "Could not reach the server.",
      });
    }
  };

  const submitCode = async (e: FormEvent) => {
    e.preventDefault();
    if (phase.kind !== "sent") return;
    const digits = code.replace(/\D/g, "");
    if (digits.length !== 6) {
      setCodeError("The code is six digits.");
      return;
    }
    const address = phase.email;
    setPhase({ kind: "checking", email: address });
    try {
      const body = await api.post<{ email: string }>("/api/auth/code", {
        email: address,
        code: digits,
      });
      await finish(body.email);
    } catch (err) {
      setCodeError(err instanceof ApiError ? err.message : "Could not reach the server.");
      setPhase({ kind: "sent", email: address });
    }
  };

  return (
    <main className="signin">
      <h1 className="wordmark signin-mark">
        <Logo size={30} />
        {APP_NAME}
      </h1>
      <p className="tagline">The human writes and the machine reads.</p>

      {phase.kind === "redeeming" && <p className="muted">Signing you in…</p>}

      {(phase.kind === "sent" || phase.kind === "checking") && (
        <>
          <p>
            A sign-in email is on its way to <strong>{phase.email}</strong>. Follow its link, or
            type its code below if you are using {APP_NAME} from your home screen. Both work once and
            expire in fifteen minutes. Check the spam folder if it does not arrive.
          </p>
          <form onSubmit={submitCode} className="signin-form">
            {codeError && <p className="error">{codeError}</p>}
            <label htmlFor="code" className="muted">
              Code from the email
            </label>
            <input
              id="code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={6}
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="123456"
            />
            <button type="submit" className="ask" disabled={phase.kind === "checking"}>
              {phase.kind === "checking" ? "Checking…" : "Sign in"}
            </button>
            <button
              type="button"
              className="text-button flush"
              onClick={() => setPhase({ kind: "form" })}
            >
              Use a different email
            </button>
          </form>
        </>
      )}

      {(phase.kind === "form" || phase.kind === "sending" || phase.kind === "error") && (
        <form onSubmit={submit} className="signin-form">
          {phase.kind === "error" && <p className="error">{phase.message}</p>}
          <label htmlFor="email" className="muted">
            Email address
          </label>
          <input
            id="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
          />
          <button type="submit" className="ask" disabled={phase.kind === "sending"}>
            {phase.kind === "sending" ? "Sending…" : "Email me a sign-in link"}
          </button>
          <p className="muted small">
            No password. Log in or create an account by clicking the link in the email.
          </p>
        </form>
      )}
    </main>
  );
}
